/**
 * SuperAdmin → Cuentas. Cada cuenta (tenant) con lo que importa: salud, empresas, usuarios
 * contra el plan, actividad del mes, respaldo y tamaño. Clic → detalle.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, RefreshCw, Building2 } from 'lucide-react';
import clsx from 'clsx';
import SuperadminLayout from '../components/SuperadminLayout';
import CreateTenantModal from '../components/CreateTenantModal';
import { panelApi } from '../utils/api';
import {
  Encabezado, Boton, Cargando, Aviso, Vacio, Salud, EstadoCuenta, inputCls,
  fNum, fColones, fBytes, hace, fFecha, fFechaHora
} from '../components/ui';

const FILTROS = [['', 'Todas'], ['active', 'Activas'], ['suspended', 'Suspendidas'], ['cancelled', 'Canceladas']];

export default function Tenants() {
  const [cuentas, setCuentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [refrescando, setRefrescando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [estado, setEstado] = useState('');
  const [crear, setCrear] = useState(false);
  const navigate = useNavigate();

  const cargar = useCallback(async (forzar = false) => {
    forzar ? setRefrescando(true) : setCargando(true);
    try {
      const r = await panelApi.resumen(forzar);
      setCuentas(r.data.data.cuentas || []);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudieron cargar las cuentas' });
    } finally {
      setCargando(false);
      setRefrescando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const lista = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    return cuentas.filter(c => (!estado || c.estado === estado)
      && (!q || [c.nombre, c.slug, c.contacto].some(v => String(v || '').toLowerCase().includes(q))));
  }, [cuentas, buscar, estado]);

  const sobreLimite = (usado, max) => max && usado > max;

  return (
    <SuperadminLayout>
      <Encabezado titulo="Cuentas" subtitulo="Cada cuenta es una base de datos con una o varias empresas">
        <Boton icono={RefreshCw} cargando={refrescando} onClick={() => cargar(true)}>Actualizar</Boton>
        <Boton variante="primario" icono={Plus} onClick={() => setCrear(true)}>Nueva cuenta</Boton>
      </Encabezado>
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex flex-col md:flex-row gap-3 p-4 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder="Buscar por nombre, identificador o correo…" className={clsx(inputCls, 'pl-9')} />
          </div>
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
            {FILTROS.map(([k, label]) => (
              <button key={k} onClick={() => setEstado(k)}
                className={clsx('px-3 py-1.5 rounded-md text-sm font-medium', estado === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900')}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {cargando ? <Cargando /> : lista.length === 0 ? (
          <Vacio icono={Building2} titulo="No hay cuentas que coincidan" texto={buscar || estado ? 'Pruebe con otro filtro.' : 'Cree la primera cuenta.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-5 py-2.5">Cuenta</th>
                  <th className="text-left font-medium px-3 py-2.5">Estado</th>
                  <th className="text-left font-medium px-3 py-2.5">Salud</th>
                  <th className="text-right font-medium px-3 py-2.5">Empresas</th>
                  <th className="text-right font-medium px-3 py-2.5">Usuarios</th>
                  <th className="text-right font-medium px-3 py-2.5">Comprobantes mes</th>
                  <th className="text-right font-medium px-3 py-2.5">Facturado mes</th>
                  <th className="text-left font-medium px-3 py-2.5">Último ingreso</th>
                  <th className="text-right font-medium px-3 py-2.5">Datos</th>
                  <th className="text-left font-medium px-3 py-2.5">Creada</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lista.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/superadmin/cuentas/${c.id}`)} className="cursor-pointer hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900">{c.nombre}</p>
                      <p className="text-xs text-gray-500">{c.slug}{c.contacto ? ` · ${c.contacto}` : ''}</p>
                    </td>
                    <td className="px-3 py-3"><EstadoCuenta estado={c.estado} /><p className="text-xs text-gray-500 mt-1">plan {c.plan}</p></td>
                    <td className="px-3 py-3"><Salud alertas={c.alertas} error={c.error} /></td>
                    <td className={clsx('px-3 py-3 text-right', sobreLimite(c.empresas, c.maxEmpresas) ? 'text-amber-700 font-medium' : 'text-gray-700')}
                      title={c.maxEmpresas ? `Plan: ${c.maxEmpresas}` : ''}>
                      {c.empresas}{c.maxEmpresas ? <span className="text-gray-400 font-normal"> / {c.maxEmpresas}</span> : ''}
                    </td>
                    <td className={clsx('px-3 py-3 text-right', sobreLimite(c.usuarios.activos, c.maxUsuarios) ? 'text-amber-700 font-medium' : 'text-gray-700')}
                      title={c.maxUsuarios ? `Plan: ${c.maxUsuarios}` : ''}>
                      {c.usuarios.activos}{c.maxUsuarios ? <span className="text-gray-400 font-normal"> / {c.maxUsuarios}</span> : ''}
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-gray-900">{fNum(c.comprobantesMes)}</td>
                    <td className="px-3 py-3 text-right text-gray-900">{fColones(c.facturadoMesCRC, true)}</td>
                    <td className="px-3 py-3 text-gray-600" title={fFechaHora(c.usuarios.ultimoLogin)}>{hace(c.usuarios.ultimoLogin)}</td>
                    <td className="px-3 py-3 text-right text-gray-600">{fBytes(c.baseBytes)}</td>
                    <td className="px-3 py-3 text-gray-600">{fFecha(c.creada)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {crear && (
        <CreateTenantModal onClose={() => setCrear(false)}
          onCreated={() => { setCrear(false); setAviso({ tipo: 'ok', texto: 'Cuenta creada' }); cargar(true); }} />
      )}
    </SuperadminLayout>
  );
}
